import { Value } from 'baseui/select';
import { capitalize } from 'common/utils/string';
import { useFormState } from 'components/elements/form';
import TableInput from 'components/elements/table/table-input';
import { ActionType } from 'components/elements/table/type';
import * as React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import useGetGuestDetailTableList from './table-column';
import {
  ActivitySaleFormType,
  ActivitySalesGuestDetailFormType,
} from '../../form-type';
import { addNewGuestDetailItem } from '../../hooks/use-get-default-values';

interface Props {
  parentIndex: number;
  isGroupedPrice: boolean;
  detailIndex: number;
}

export default function ActivityGuestDetailGroupedTable(props: Props) {
  const { isGroupedPrice } = props;
  const { editable } = useFormState();
  const { control, getValues, trigger, clearErrors, setValue } =
    useFormContext<ActivitySaleFormType>();

  const guestName =
    `activitySales.${props.parentIndex}.activitySaleGuests` as const;

  const detailName =
    `activitySales.${props.parentIndex}.activitySaleGuests.${props.detailIndex}.activitySaleGuestDetails` as const;

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: detailName,
    keyName: '__customId',
  });

  const columns = useGetGuestDetailTableList(isGroupedPrice);

  const dispatch = React.useCallback(
    (action) => {
      const type = action.type as ActionType;
      const columnId =
        action.columnId as keyof ActivitySalesGuestDetailFormType;
      const currentRowIndex = action.rowIndex as number;
      const details = getValues(detailName);
      const detail = details[currentRowIndex];
      const value = action.value;
      const extra = action.extra as Value;

      const otherVal: () => Partial<ActivitySalesGuestDetailFormType> = () => {
        if (columnId === 'activityId') {
          const val = extra?.[0]?.extra;
          return {
            type: capitalize(val?.type),
            activityName: val?.name,
            activityCode: val?.code,
          };
        }
        if (columnId === 'price') {
          return {
            totalPrice: (value || 0) * (detail.qty || 0),
          };
        }
        if (columnId === 'qty') {
          return {
            totalPrice: (detail.price || 0) * (value || 0),
          };
        }
        return {};
      };

      if (type === 'addRow') {
        append(addNewGuestDetailItem());
        trigger(detailName);
      } else if (type === 'removeRow') {
        remove(currentRowIndex);
        trigger(detailName);
      } else if (type === 'update_cell') {
        const row = {
          ...detail,
          [columnId]: value,
          ...otherVal(),
        };
        update(currentRowIndex, row);
        clearErrors(`${detailName}`);

        if (isGroupedPrice) {
          const guests = getValues(guestName).map((guest, _, arr) => {
            const activitySaleGuestDetails =
              arr?.[0]?.activitySaleGuestDetails?.map((detail, index) => {
                if (currentRowIndex === index) {
                  return row;
                }
                return detail;
              }) ?? [];

            return {
              ...guest,
              activitySaleGuestDetails,
            };
          });

          setValue(guestName, guests);
          clearErrors(guestName);
        }
      }
    },
    [
      getValues,
      detailName,
      append,
      trigger,
      remove,
      update,
      clearErrors,
      isGroupedPrice,
      guestName,
      setValue,
    ],
  );

  return (
    <div style={{ width: '100%' }}>
      <TableInput
        columns={columns}
        data={fields}
        dispatch={dispatch}
        disabled={!editable}
        name={detailName}
        uniqueRowKey="customId"
      />
    </div>
  );
}
